import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Send } from "lucide-react";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import { Badge, Button, Card, DataTable, Field, Input, PageHeader, Select, Textarea } from "@/components/ui-kit";

const TYPES = [
  ["preco", "Preço"],
  ["estoque", "Estoque"],
  ["cadastro", "Cadastro"],
  ["outro", "Outro"],
];

/* Mesmos três estados do lado do admin, agora em tom semântico: pendente =
   atenção (ainda vale o valor antigo), aprovado = o admin acatou, recusado =
   negado. "Aprovado" é a decisão, não a garantia de que já foi aplicado. */
const STATUS_TONE = {
  pendente: "warning",
  aprovado: "success",
  recusado: "danger",
};

const EMPTY = { type: "preco", entity: "", note: "" };

export default function ChangeRequests() {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  // O vazio da lista manda a pessoa para o formulário acima em vez de só avisar
  // que não há nada — é a única ação possível nesta tela.
  const noteRef = useRef(null);

  const {
    items, total, page, lastPage, setPage, isLoading: loading, isError: error, refetch: load,
  } = usePaginatedList("/change-requests/my");

  const submit = async () => {
    if (!form.note.trim()) { toast.error("Descreva a alteração solicitada"); return; }
    setSaving(true);
    try {
      await api.post("/change-requests", form);
      toast.success("Solicitação enviada ao administrador");
      setForm(EMPTY);
      // A nova solicitação entra no topo (mais recente): volta para a página 1
      // para o vendedor ver o que acabou de enviar.
      setPage(1);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="change-requests-page">
      <PageHeader
        eyebrow="Operação"
        title="Solicitar Alteração"
        icon={Inbox}
        description="Peça ao administrador o que o balcão não muda sozinho: preço, estoque ou cadastro. Até ele aprovar e aplicar a mudança, o produto continua sendo vendido como está hoje."
      />

      <Card className="p-6 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Tipo" required>
            <Select value={form.type} onChange={set("type")} data-testid="cr-type">
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </Field>
          <Field label="Produto / item" required>
            <Input value={form.entity} onChange={set("entity")} placeholder="Ex.: Órtese punho M" data-testid="cr-entity" />
          </Field>
        </div>
        <Field label="Descrição da solicitação" required>
          <Textarea ref={noteRef} value={form.note} onChange={set("note")} rows={3} data-testid="cr-note" placeholder="Descreva a alteração que precisa (novo preço, ajuste de estoque, correção de cadastro...)" />
        </Field>
        <Button icon={Send} size="lg" onClick={submit} loading={saving} data-testid="cr-submit">
          {saving ? "Enviando..." : "Enviar solicitação"}
        </Button>
      </Card>

      {/* O título saiu de dentro do card: o DataTable já desenha a própria
          superfície, e um card dentro do outro empilharia duas bordas. */}
      <section className="space-y-3">
        <h2 className="font-heading text-sm font-semibold text-slate-900">Minhas solicitações</h2>
        <DataTable
        rowTestId="cr-row"
          caption="Minhas solicitações de alteração"
          rows={items}
          rowKey={(c) => c.id}
          loading={loading}
          error={error}
          onRetry={load}
          empty={{
            icon: Inbox,
            title: "Nenhuma solicitação",
            desc: "As alterações que você pedir aparecem aqui com o status da aprovação.",
            action: (
              <Button variant="secondary" onClick={() => noteRef.current?.focus()}>
                Descrever a primeira solicitação
              </Button>
            ),
            testId: "cr-empty",
          }}
          columns={[
            {
              key: "solicitacao", header: "Solicitação",
              cell: (c) => (
                <div>
                  <p className="font-medium text-slate-900 capitalize">{c.type}{c.entity ? ` · ${c.entity}` : ""}</p>
                  <p className="text-xs text-slate-500">{c.note}</p>
                  {/* A resposta do admin é o que explica um "recusado". */}
                  {c.review_note && <p className="text-xs text-slate-400 mt-0.5">Resposta: {c.review_note}</p>}
                </div>
              ),
            },
            {
              key: "status", header: "Status", align: "right",
              cell: (c) => <Badge tone={STATUS_TONE[c.status]} className="capitalize">{c.status}</Badge>,
            },
          ]}
        />
        <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="solicitações" />
      </section>
    </div>
  );
}
